import React from "react";
import { useAuth, startGoogleLogin } from "@/hooks/useAuth";
import { useEarlyBird } from "@/hooks/useEarlyBird";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Mic, Brain, MessageCircle, Volume2, Languages, Sparkles, Plug, Check, Phone, Play, Pause, Loader2, Send, X, Flame } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c9315c06-d46b-4aaa-b075-670bd250583d/images/d8eeea6428ad6056e37b7ab407594a90b9d3309cc4686f480dd27f0fc1c4629a.png";
const FEATURE_IMG = "https://static.prod-images.emergentagent.com/jobs/c9315c06-d46b-4aaa-b075-670bd250583d/images/3861f69fe0918118f3a1e0a0b01a2a6ab8dab8d2731d272f3d3ff499e53e0fca.png";
const CONTEXT_IMG = "https://images.unsplash.com/photo-1423784346385-c1d4dac9893a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBob2xkaW5nJTIwcGhvbmUlMjBkYXJrJTIwd2FybSUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzkwNjkxMDB8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const { state: earlyBird } = useEarlyBird();
  const navigate = useNavigate();

  // Capture referral attribution from ?ref=widget&u=XXX into localStorage (90-day expiry)
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      const refUser = params.get("u");
      if (ref && refUser) {
        localStorage.setItem("agentreply_ref", JSON.stringify({
          source: ref, user_id: refUser, ts: Date.now(),
        }));
      }
    } catch { /* ignore */ }
  }, []);

  const handleCTA = () => {
    if (user) navigate("/dashboard");
    else navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <EarlyBirdStrip state={earlyBird} />
      <Navbar user={user} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 pt-20 pb-24 md:pt-28 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D95B38] mb-8 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D95B38]" />
              The short answer
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter font-light">
              <span className="italic text-[#D95B38]">Yes.</span><br />
              You can build a<br />
              custom AI voice agent<br />
              <span className="italic">for WhatsApp.</span>
            </h1>
            <p className="font-mono text-base text-[#A39A8F] mt-8 max-w-xl leading-relaxed">
              AgentReply lets you spin one up in <span className="text-[#F5F2EB]">under five minutes</span>.
              Choose a personality, a voice, a language. Drop in knowledge.
              Your agent listens to <span className="text-[#F5F2EB]">voice notes &amp; text</span>, thinks, and replies — by voice or text, the way your customers prefer.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-12">
              <button
                onClick={handleCTA}
                data-testid="hero-cta-build"
                className="font-mono text-xs uppercase tracking-[0.25em] text-[#F5F2EB] bg-[#D95B38] px-8 py-4 btn-press flex items-center gap-3"
              >
                Build your agent <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <a
                href="#how"
                data-testid="hero-cta-how"
                className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] px-2 py-4 border-b border-transparent hover:border-[#F5F2EB] transition-colors"
              >
                See how it works
              </a>
            </div>

            <div className="mt-16 flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em] text-[#A39A8F]">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 inline-block pulse-green rounded-full" />
                Live demo
              </div>
              <div>~5 min setup</div>
              <div className="hidden sm:block">No code</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square bg-[#1A1715] border border-[#F5F2EB]/10 overflow-hidden">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0D0C] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#0F0D0C] border border-[#F5F2EB]/15 p-5 max-w-[240px] hidden md:block">
              <div className="flex items-center gap-2 mb-2">
                <Mic className="w-4 h-4 text-[#D95B38]" strokeWidth={1.5} />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Voice in</div>
              </div>
              <div className="font-serif text-sm italic text-[#F5F2EB]">
                "Hey, can you book a table for two at 8?"
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-[#F5F2EB]/10 py-6 overflow-hidden">
          <div className="marquee-track font-serif italic text-3xl text-[#F5F2EB]/30">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex items-center gap-12 pr-12">
                <span>Voice in</span><span className="text-[#D95B38]">·</span>
                <span>Transcribe</span><span className="text-[#D95B38]">·</span>
                <span>Reason</span><span className="text-[#D95B38]">·</span>
                <span>Speak back</span><span className="text-[#D95B38]">·</span>
                <span>Across 30+ languages</span><span className="text-[#D95B38]">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="mb-16 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-4">01 / What it does</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">
            Everything your agent needs to <span className="italic text-[#D95B38]">sound human</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <FeatureCard
            className="md:col-span-7"
            icon={<MessageCircle className="w-8 h-8" strokeWidth={1.2} />}
            title="WhatsApp-native"
            body="Receives voice notes &amp; text. Sends voice notes &amp; text. Works in 1:1 chats and groups. Your agent shows up like a real contact."
            tag="Channel"
          />
          <FeatureCard
            className="md:col-span-5"
            icon={<Brain className="w-8 h-8" strokeWidth={1.2} />}
            title="Claude Sonnet 4.5 brain"
            body="State-of-the-art reasoning. Custom system prompt. Your knowledge base."
            tag="LLM"
          />
          <FeatureCard
            className="md:col-span-4"
            icon={<Volume2 className="w-8 h-8" strokeWidth={1.2} />}
            title="Studio-grade voices"
            body="9 OpenAI voices free, 6 ElevenLabs premium voices on demand ($4.99/mo). Or upload 30 seconds of audio to clone any voice — your own."
            tag="TTS"
          />
          <FeatureCard
            className="md:col-span-4"
            icon={<Languages className="w-8 h-8" strokeWidth={1.2} />}
            title="30+ languages"
            body="Whisper transcribes any language. Reply in the user's tongue."
            tag="STT"
          />
          <FeatureCard
            className="md:col-span-4"
            icon={<Sparkles className="w-8 h-8" strokeWidth={1.2} />}
            title="Persona builder"
            body="Tone, style, rules. Saved per agent. Editable in one click."
            tag="Custom"
          />
          {/* Coming soon — Live phone calls */}
          <div data-testid="feature-coming-soon-calls"
            className="md:col-span-12 relative border border-[#D95B38]/40 bg-[#1A1715] p-8 md:p-10 overflow-hidden">
            <div className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] border border-[#D95B38] px-3 py-1">
              Coming soon
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
              <div className="w-16 h-16 border border-[#D95B38] flex items-center justify-center shrink-0">
                <Phone className="w-7 h-7 text-[#D95B38]" strokeWidth={1.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Live phone calls</div>
                <div className="font-serif text-2xl md:text-3xl mb-3">Your agent picks up the phone.</div>
                <p className="font-mono text-sm text-[#E0D8CA] leading-relaxed max-w-2xl">
                  Real-time voice conversations on a regular phone number — answer calls, qualify leads, book appointments. ~500ms response latency, sounds like a human. Will launch as an add-on to Pro plans.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VOICE DEMO — hear the premium ElevenLabs voices */}
      <VoiceDemo />

      {/* PRESET LIVE DEMO — try a real agent before signing up */}
      <PresetDemo />

      {/* HOW IT WORKS */}
      <section id="how" className="relative">
        <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-4">02 / How</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mb-8">
                Four steps. <span className="italic">No code.</span>
              </h2>
              <div className="aspect-[4/5] border border-[#F5F2EB]/10 overflow-hidden">
                <img src={FEATURE_IMG} alt="" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-2">
              {[
                { n: "01", t: "User sends a voice note", b: "WhatsApp delivers an .ogg/.opus blob to your agent's inbox." },
                { n: "02", t: "Whisper transcribes", b: "Speech → text in milliseconds. Language auto-detected." },
                { n: "03", t: "Claude thinks", b: "Reads your persona + knowledge base. Drafts a short, natural reply." },
                { n: "04", t: "OpenAI TTS speaks back", b: "Reply rendered as a voice note in the voice you chose. Posted back to chat." },
              ].map((s) => (
                <div key={s.n} className="group border-b border-[#F5F2EB]/10 py-8 grid grid-cols-12 gap-6 items-baseline hover:border-[#D95B38]/40 transition-colors">
                  <div className="col-span-2 font-mono text-xs uppercase tracking-[0.3em] text-[#D95B38]">{s.n}</div>
                  <div className="col-span-10">
                    <div className="font-serif text-2xl mb-2 group-hover:italic transition-all">{s.t}</div>
                    <div className="font-mono text-sm text-[#A39A8F] leading-relaxed">{s.b}</div>
                  </div>
                </div>
              ))}
              <div className="pt-8">
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] mb-2">Build time</div>
                <div className="font-serif text-4xl italic text-[#F5F2EB]">~5 minutes</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <FoundingMembersStrip />
      <Pricing onCheckout={handleCTA} user={user} earlyBird={earlyBird} />
      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24">
        <FaqSection />
      </section>

      {/* CTA BAND */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24">
        <div className="border border-[#F5F2EB]/15 p-12 md:p-20 bg-[#1A1715] flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
          <div>
            <h3 className="font-serif text-3xl sm:text-4xl leading-tight max-w-xl">
              Stop reading. <span className="italic text-[#D95B38]">Build your agent now.</span>
            </h3>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] mt-3">Free · 5 minutes · No credit card</div>
          </div>
          <button
            onClick={handleCTA}
            data-testid="bottom-cta"
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#F5F2EB] bg-[#D95B38] px-8 py-4 btn-press flex items-center gap-3"
          >
            <Plug className="w-4 h-4" strokeWidth={1.5} /> Get started
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PresetDemo() {
  const [presets, setPresets] = React.useState([]);
  const [active, setActive] = React.useState(null); // slug currently being chatted with
  const [messages, setMessages] = React.useState([]); // [{role, text}]
  const [sessionId, setSessionId] = React.useState(null);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [bannerDismissed, setBannerDismissed] = React.useState(false);
  const ctaShownLoggedRef = React.useRef(false);  // dedup the cta_shown event per session
  const scrollRef = React.useRef(null);
  const { state: earlyBird } = useEarlyBird();

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presets`)
      .then((r) => r.json())
      .then((d) => setPresets(d.presets || []))
      .catch(() => { /* section just won't render */ });
  }, []);

  // Auto-scroll chat to latest message
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const activePreset = presets.find((p) => p.slug === active);
  const userMessageCount = messages.filter((m) => m.role === "user").length;
  const showConversionBanner = active && userMessageCount >= 3 && !bannerDismissed;

  // Fire cta_shown ONCE per (session, preset) when banner first becomes visible.
  // Idempotent on the backend too, but we also dedup client-side to save a roundtrip.
  React.useEffect(() => {
    if (showConversionBanner && !ctaShownLoggedRef.current && sessionId) {
      ctaShownLoggedRef.current = true;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presets/demo-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: active, event: "cta_shown",
          session_id: sessionId, message_count: userMessageCount,
        }),
      }).catch(() => { /* analytics never blocks UX */ });
    }
  }, [showConversionBanner, sessionId, active, userMessageCount]);

  const logCtaClick = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presets/demo-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug: active, event: "cta_click",
        session_id: sessionId, message_count: userMessageCount,
      }),
    }).catch(() => { /* ignore */ });
  };

  const openPreset = (slug) => {
    setActive(slug);
    setMessages([]);
    setSessionId(null);
    setError(null);
    setInput("");
    setBannerDismissed(false);
    ctaShownLoggedRef.current = false;
  };

  const closePreset = () => {
    setActive(null);
    setMessages([]);
    setSessionId(null);
    setError(null);
    setBannerDismissed(false);
    ctaShownLoggedRef.current = false;
  };

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    setError(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presets/${active}/demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Demo failed (HTTP ${r.status})`);
      }
      const data = await r.json();
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, name: data.preset?.name }]);
    } catch (e) {
      setError(e.message || "Demo failed. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (presets.length === 0) return null;

  return (
    <section id="preset-demo" data-testid="preset-demo-section" className="border-b border-[#F5F2EB]/10">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-10">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-4">01.7 / Talk to one</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[0.95]">
              Pick a use-case.<br />
              <span className="italic text-[#D95B38]">Have a real conversation.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-2">
            <p className="font-mono text-sm text-[#A39A8F] leading-relaxed">
              These are the same presets that come with every account. Try one live — no signup, no email,
              just type. The agent below is identical to what you'd ship to customers in 60 seconds.
            </p>
          </div>
        </div>

        {/* Preset card grid OR active chat */}
        {!active ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="preset-demo-grid">
            {presets.map((p) => (
              <button
                key={p.slug}
                onClick={() => openPreset(p.slug)}
                data-testid={`preset-demo-card-${p.slug}`}
                className="group text-left p-5 border border-[#F5F2EB]/10 bg-[#0F0D0C] hover:border-[#D95B38] hover:bg-[#1A1715] transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl shrink-0">{p.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-xl text-[#F5F2EB]">{p.label}</div>
                    <div className="font-mono text-[10px] text-[#A39A8F] mt-1.5 leading-snug">{p.tagline}</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#F5F2EB]/10 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F]">
                    {p.best_for.slice(0, 2).join(" · ")}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]/0 group-hover:text-[#D95B38] transition-colors">
                    Try it →
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="border border-[#D95B38]/40 bg-[#0F0D0C]" data-testid="preset-demo-chat">
            {/* Chat header */}
            <div className="flex items-center justify-between p-4 border-b border-[#F5F2EB]/10 bg-[#1A1715]">
              <div className="flex items-center gap-3">
                <div className="text-2xl">{activePreset?.icon}</div>
                <div>
                  <div className="font-serif text-lg text-[#F5F2EB]">{activePreset?.suggested_name || activePreset?.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">{activePreset?.label}</div>
                </div>
              </div>
              <button
                onClick={closePreset}
                data-testid="preset-demo-close"
                className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38] flex items-center gap-2 px-3 py-2 transition-colors"
              >
                Try another <X className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="h-[420px] overflow-y-auto p-6 space-y-4"
              data-testid="preset-demo-messages"
            >
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-6">Try asking</div>
                  <div className="space-y-2 max-w-md mx-auto">
                    {(activePreset?.starter_questions || []).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(q)}
                        data-testid={`preset-demo-starter-${i}`}
                        className="block w-full text-left p-3 border border-[#F5F2EB]/15 bg-[#1A1715] hover:border-[#D95B38] hover:bg-[#221C19] font-mono text-sm text-[#F5F2EB] transition-colors"
                      >
                        "{q}"
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`preset-demo-message-${m.role}-${i}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 ${
                      m.role === "user"
                        ? "bg-[#D95B38] text-[#F5F2EB]"
                        : "bg-[#1A1715] border border-[#F5F2EB]/10 text-[#F5F2EB]"
                    }`}
                  >
                    {m.role === "assistant" && m.name && (
                      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D95B38] mb-1.5">{m.name}</div>
                    )}
                    <div className="font-mono text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="preset-demo-typing">
                  <div className="bg-[#1A1715] border border-[#F5F2EB]/10 px-4 py-3">
                    <div className="flex gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#A39A8F] animate-pulse"></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#A39A8F] animate-pulse" style={{ animationDelay: "0.2s" }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#A39A8F] animate-pulse" style={{ animationDelay: "0.4s" }}></span>
                    </div>
                  </div>
                </div>
              )}
              {error && (
                <div data-testid="preset-demo-error" className="text-center font-mono text-xs text-[#D95B38] py-2">{error}</div>
              )}
            </div>

            {/* Conversion banner — soft sell once visitor is clearly engaged (3+ messages sent) */}
            {showConversionBanner && (
              <div data-testid="preset-demo-cta-banner" className="flex items-center gap-4 px-4 py-4 border-t border-[#D95B38]/40 bg-gradient-to-r from-[#D95B38]/15 via-[#D95B38]/5 to-transparent">
                <div className="text-2xl">{activePreset?.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#F5F2EB]">
                    Liked {activePreset?.suggested_name}? Build your own in 60 seconds.
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mt-1">
                    {earlyBird?.active ? (
                      <>
                        <span className="text-[#D95B38]">${earlyBird.pricing?.monthly?.current?.toFixed(2)}/mo founding price</span> · {earlyBird.quota_remaining} of {earlyBird.quota} slots left · 14-day free trial
                      </>
                    ) : (
                      "14-day free trial · no card required"
                    )}
                  </div>
                </div>
                <a
                  href={`/signup?from=demo&preset=${encodeURIComponent(active)}`}
                  onClick={logCtaClick}
                  data-testid="preset-demo-cta-signup"
                  className="font-mono text-xs uppercase tracking-[0.25em] text-[#F5F2EB] bg-[#D95B38] hover:bg-[#D95B38]/90 px-5 py-3 transition-colors whitespace-nowrap"
                >
                  {earlyBird?.active ? "Claim founding price →" : "Start free trial →"}
                </a>
                <button
                  onClick={() => setBannerDismissed(true)}
                  data-testid="preset-demo-cta-dismiss"
                  title="Dismiss"
                  className="text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex items-center gap-3 p-4 border-t border-[#F5F2EB]/10 bg-[#1A1715]"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Message ${activePreset?.suggested_name || "the agent"}…`}
                maxLength={500}
                data-testid="preset-demo-input"
                disabled={sending}
                className="flex-1 bg-transparent font-mono text-sm text-[#F5F2EB] placeholder-[#A39A8F]/50 focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="preset-demo-send"
                className="w-10 h-10 flex items-center justify-center bg-[#D95B38] text-[#F5F2EB] disabled:opacity-30 hover:bg-[#D95B38]/90 transition-colors"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.8} />}
              </button>
            </form>

            <div className="px-4 py-2 border-t border-[#F5F2EB]/10 bg-[#0F0D0C] font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]/60 text-center">
              Live demo · 14-day free trial unlocks the real product
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function VoiceDemo() {
  const [voices, setVoices] = React.useState([]);
  const [sampleText, setSampleText] = React.useState("");
  const [activeId, setActiveId] = React.useState(null);   // voice currently playing
  const [loadingId, setLoadingId] = React.useState(null); // voice being fetched
  const [error, setError] = React.useState(null);
  const audioRef = React.useRef(null);
  const cacheRef = React.useRef({}); // voice_id → audio_b64

  // Load catalogue once on mount
  React.useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/voices/demo`;
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) throw new Error("catalogue unavailable");
        const data = await r.json();
        setVoices(data.voices || []);
        setSampleText(data.sample_text || "");
      } catch {
        // Silent fail — section just won't render anything
      }
    })();
    return () => ctrl.abort();
  }, []);

  const stopPlayback = React.useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* ignore */ }
    }
    setActiveId(null);
  }, []);

  React.useEffect(() => () => stopPlayback(), [stopPlayback]);

  const playVoice = async (voiceId) => {
    setError(null);
    if (activeId === voiceId) { stopPlayback(); return; }
    stopPlayback();
    setLoadingId(voiceId);
    try {
      let audioB64 = cacheRef.current[voiceId];
      if (!audioB64) {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voices/demo/${encodeURIComponent(voiceId)}`);
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || "Could not load sample");
        }
        const data = await r.json();
        audioB64 = data.audio_b64;
        cacheRef.current[voiceId] = audioB64;
      }
      const audio = new Audio(`data:audio/mpeg;base64,${audioB64}`);
      audioRef.current = audio;
      audio.onended = () => setActiveId(null);
      audio.onerror = () => { setActiveId(null); setError("Playback failed."); };
      try {
        await audio.play();
      } catch (playErr) {
        // Autoplay-policy or no-audio-device (e.g. some embedded browsers) — silent fallback
        if (playErr?.name !== "NotAllowedError" && playErr?.name !== "NotSupportedError") {
          setError(playErr?.message || "Could not play sample.");
        }
        setActiveId(null);
        return;
      }
      setActiveId(voiceId);
    } catch (e) {
      setError(e.message || "Could not play sample.");
    } finally {
      setLoadingId(null);
    }
  };

  if (voices.length === 0) return null;

  return (
    <section id="voice-demo" data-testid="voice-demo-section" className="border-y border-[#F5F2EB]/10 bg-[#1A1715]">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-4">01.5 / Hear it</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[0.95] mb-6">
              Press play.<br />
              <span className="italic text-[#D95B38]">No sign-up.</span>
            </h2>
            <p className="font-mono text-sm text-[#A39A8F] leading-relaxed max-w-md">
              These are the premium <span className="text-[#F5F2EB]">ElevenLabs</span> voices your agent can use.
              Add the upgrade for <span className="text-[#F5F2EB]">$4.99/mo per agent</span> — or clone your own voice from a 30-second sample.
            </p>
            {sampleText && (
              <div className="mt-8 border-l-2 border-[#D95B38]/60 pl-5 py-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">They're saying</div>
                <div className="font-serif italic text-lg text-[#F5F2EB] leading-snug">"{sampleText}"</div>
              </div>
            )}
            {error && (
              <div data-testid="voice-demo-error" className="mt-6 font-mono text-xs text-[#D95B38]">{error}</div>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {voices.map((v) => {
              const isPlaying = activeId === v.id;
              const isLoading = loadingId === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => playVoice(v.id)}
                  data-testid={`voice-demo-card-${v.label.toLowerCase()}`}
                  className={`group text-left p-5 border bg-[#0F0D0C] transition-all duration-200 flex items-center gap-4 ${
                    isPlaying ? "border-[#D95B38] bg-[#221C19]" : "border-[#F5F2EB]/10 hover:border-[#D95B38]/60"
                  }`}
                >
                  <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                    isPlaying ? "bg-[#D95B38] text-[#F5F2EB]" : "bg-[#1A1715] text-[#D95B38] group-hover:bg-[#D95B38] group-hover:text-[#F5F2EB]"
                  }`}>
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                      : isPlaying ? <Pause className="w-4 h-4" strokeWidth={1.8} />
                      : <Play className="w-4 h-4 translate-x-[1px]" strokeWidth={1.8} />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-serif text-xl text-[#F5F2EB]">{v.label}</span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F] mt-1 truncate">{v.description}</span>
                  </span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${isPlaying ? "text-[#D95B38]" : "text-[#A39A8F]/0 group-hover:text-[#A39A8F]"} transition-colors`}>
                    {isPlaying ? "Playing" : "Play →"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ className, icon, title, body, tag }) {
  return (
    <div
      className={`${className} group bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10 transition-all duration-300 hover:border-[#D95B38]/50 hover:bg-[#221C19]`}
    >
      <div className="flex justify-between items-start mb-12">
        <div className="text-[#D95B38]">{icon}</div>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">{tag}</span>
      </div>
      <div className="font-serif text-2xl mb-3">{title}</div>
      <div className="font-mono text-sm text-[#A39A8F] leading-relaxed">{body}</div>
    </div>
  );
}

function EarlyBirdStrip({ state }) {
  if (!state || !state.active) return null;
  const remaining = state.quota_remaining;
  const monthly = state.pricing?.monthly;
  // Calm-then-loud — pulse intensity ramps as slots dwindle
  const urgent = remaining <= 25;
  return (
    <div
      data-testid="early-bird-strip"
      className={`relative border-b border-[#D95B38]/40 ${urgent ? "bg-[#2A1410]" : "bg-[#221C19]"}`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-2.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Flame className={`w-3.5 h-3.5 shrink-0 text-[#D95B38] ${urgent ? "animate-pulse" : ""}`} strokeWidth={2} />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F5F2EB]">
            Founding pricing
          </span>
          <span className="hidden sm:inline font-mono text-[11px] text-[#A39A8F] truncate">
            ${monthly?.current?.toFixed(2)}/mo locked in forever ·
            <span className={urgent ? "text-[#D95B38] font-semibold ml-1" : "ml-1"} data-testid="eb-strip-remaining">
              {remaining} of {state.quota} slot{remaining === 1 ? "" : "s"} left
            </span>
          </span>
          {/* Mobile compact label */}
          <span className="sm:hidden font-mono text-[11px] text-[#A39A8F]">
            <span className={urgent ? "text-[#D95B38] font-semibold" : ""}>{remaining}</span>/{state.quota} slots
          </span>
        </div>
        <a
          href="#pricing"
          data-testid="eb-strip-cta"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:text-[#F5F2EB] transition-colors whitespace-nowrap"
        >
          Claim →
        </a>
      </div>
    </div>
  );
}

function FoundingMembersStrip() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    const ctrl = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/founding-members`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then(setData)
      .catch(() => { /* silent */ });
    return () => ctrl.abort();
  }, []);

  // Hide entirely until we have at least 1 founding member — empty social proof is anti-proof
  if (!data || !data.count || data.count < 1) return null;

  const recent = data.recent || [];
  const remainder = Math.max(0, data.count - recent.length);
  const namesShown = recent.map((m) => m.display_name);
  const namesText = namesShown.length === 1
    ? namesShown[0]
    : namesShown.length === 2
      ? `${namesShown[0]} and ${namesShown[1]}`
      : `${namesShown.slice(0, -1).join(", ")}, and ${namesShown.slice(-1)[0]}`;

  return (
    <div
      data-testid="founding-members-strip"
      className="border-y border-[#F5F2EB]/10 bg-[#0F0D0C]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-6 flex flex-col sm:flex-row sm:items-center justify-center gap-4 sm:gap-6">
        <div className="flex -space-x-2 shrink-0 justify-center" data-testid="founding-members-avatars">
          {recent.map((m, i) => (
            <div
              key={i}
              data-testid={`founding-avatar-${i}`}
              title={m.display_name}
              style={{ background: `hsl(${m.color_hue}, 55%, 32%)` }}
              className="w-9 h-9 rounded-full border-2 border-[#0F0D0C] flex items-center justify-center font-mono text-xs uppercase tracking-wide text-[#F5F2EB]"
            >
              {m.initial}
            </div>
          ))}
        </div>
        <div className="font-mono text-xs text-[#A39A8F] text-center sm:text-left leading-relaxed">
          <span className="text-[#F5F2EB]" data-testid="founding-members-names">{namesText}</span>
          {remainder > 0 && (
            <> and <span className="text-[#D95B38]">{remainder} other founding member{remainder === 1 ? "" : "s"}</span></>
          )}
          <span className="text-[#A39A8F]"> locked in their rate for life.</span>
        </div>
      </div>
    </div>
  );
}

function Pricing({ onCheckout, user, earlyBird }) {
  const [period, setPeriod] = React.useState("monthly"); // monthly | yearly | lifetime
  const [loading, setLoading] = React.useState(false);
  // Per-click idempotency key — generated fresh per click to dedupe rapid double-clicks
  // while still allowing intentional retries to create a new session.
  const idemRef = React.useRef(null);

  const startCheckout = async (planId) => {
    if (!user) { onCheckout(); return; }
    if (loading) return;  // hard gate: ignore re-clicks while previous is in flight
    setLoading(true);
    try {
      const api = (await import("@/lib/api")).default;
      // Read promo code from URL ?promo=XXX (set by rewards card link or pasted manually)
      const params = new URLSearchParams(window.location.search);
      const promoCode = params.get("promo") || null;
      // Generate idempotency key per click — survives StrictMode double-render + accidental re-clicks
      if (!idemRef.current) {
        idemRef.current = (window.crypto?.randomUUID?.() ||
                           `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      }
      const res = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
        promo_code: promoCode,
        idempotency_key: idemRef.current,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Try again.");
      // Clear idempotency key on error so the user can start fresh
      idemRef.current = null;
      setLoading(false);
    }
  };

  const ebActive = earlyBird?.active;
  const ebPricing = earlyBird?.pricing || {};
  const REG_PRICES = { monthly: "$9.99", yearly: "$70", lifetime: "$250" };
  const proPrice = ebActive
    ? `$${(ebPricing[period]?.current ?? 0).toFixed(period === "lifetime" || period === "yearly" ? 0 : 2)}`
    : REG_PRICES[period];
  const proPriceStrike = ebActive ? REG_PRICES[period] : null;
  const proUnit = period === "monthly" ? "/ month" : period === "yearly" ? "/ year" : "one-time, forever";
  const proSavings = ebActive
    ? `Save ${ebPricing[period]?.discount_pct}% · ${earlyBird.quota_remaining} founding slot${earlyBird.quota_remaining === 1 ? "" : "s"} left`
    : (period === "yearly" ? "Save 42% vs monthly" : period === "lifetime" ? "Pay once. Use forever." : null);

  return (
    <section id="pricing" className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24">
      <div className="mb-10 max-w-3xl">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-4">03 / Pricing</div>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">
          One plan. <span className="italic text-[#D95B38]">Pick your rhythm.</span>
        </h2>
      </div>

      {/* Period toggle */}
      <div className="inline-flex border border-[#F5F2EB]/15 mb-10" data-testid="pricing-period-toggle">
        {[
          { k: "monthly", l: "Monthly" },
          { k: "yearly", l: "Yearly" },
          { k: "lifetime", l: "Lifetime" },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setPeriod(p.k)}
            data-testid={`period-${p.k}`}
            className={`px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${period === p.k ? "bg-[#D95B38] text-[#F5F2EB]" : "text-[#A39A8F] hover:text-[#F5F2EB]"}`}
          >
            {p.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Free Trial */}
        <div data-testid="pricing-trial" className="p-8 md:p-10 border border-[#F5F2EB]/10 bg-[#1A1715] flex flex-col">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">Free trial</div>
          <div className="font-serif text-6xl mt-4">14<span className="font-mono text-sm text-[#A39A8F] ml-2">days · no card</span></div>
          <ul className="mt-8 space-y-3 font-mono text-sm text-[#E0D8CA] flex-1">
            {[
              "Full Pro access for 14 days",
              "Unlimited voice agents",
              "All 9 voices, 30+ languages",
              "WhatsApp + web chat + embed widget",
              "Knowledge base · contact CRM · usage stats",
            ].map((l) => (
              <li key={l} className="flex gap-2 items-start"><Check className="w-4 h-4 text-[#D95B38] mt-0.5 shrink-0" strokeWidth={1.5} />{l}</li>
            ))}
          </ul>
          <button
            onClick={onCheckout}
            data-testid="pricing-cta-trial"
            className="mt-8 font-mono text-xs uppercase tracking-[0.25em] py-3 border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
          >
            Start free trial →
          </button>
        </div>

        {/* Pro */}
        <div data-testid="pricing-pro" className="p-8 md:p-10 border border-[#D95B38] bg-[#1A1715] flex flex-col relative">
          <div className="absolute -top-3 left-8 bg-[#D95B38] text-[#F5F2EB] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1">
            {ebActive ? "Founding price" : "Most popular"}
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">Pro</div>
          <div className="font-serif text-6xl mt-4 flex items-baseline gap-3 flex-wrap">
            <span data-testid="pricing-price-current">{proPrice}</span>
            {proPriceStrike && (
              <span data-testid="pricing-price-strike" className="font-serif text-2xl text-[#A39A8F] line-through decoration-[#A39A8F]/60">{proPriceStrike}</span>
            )}
            <span className="font-mono text-sm text-[#A39A8F]">{proUnit}</span>
          </div>
          {proSavings && (
            <div data-testid="pricing-savings" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] mt-2">{proSavings}</div>
          )}
          {ebActive && (
            <div data-testid="pricing-locked-note" className="font-mono text-[10px] text-[#A39A8F] mt-3 leading-relaxed border-l-2 border-[#D95B38]/60 pl-3">
              Founding pricing is <span className="text-[#F5F2EB]">locked in forever</span> — your rate never goes up, even when we move to {REG_PRICES[period]} for everyone else.
            </div>
          )}
          <ul className="mt-8 space-y-3 font-mono text-sm text-[#E0D8CA] flex-1">
            {[
              "Unlimited voice agents",
              "10,000 replies / month",
              "Knowledge base (PDF, CSV, TXT)",
              "Web chat page + embed widget",
              "WhatsApp integration (when ready)",
              "Priority support",
            ].map((l) => (
              <li key={l} className="flex gap-2 items-start"><Check className="w-4 h-4 text-[#D95B38] mt-0.5 shrink-0" strokeWidth={1.5} />{l}</li>
            ))}
          </ul>
          <button
            onClick={() => startCheckout(period)}
            disabled={loading}
            data-testid="pricing-cta-pro"
            className="mt-8 font-mono text-xs uppercase tracking-[0.25em] py-3 bg-[#D95B38] text-[#F5F2EB] btn-press disabled:opacity-60"
          >
            {loading ? "Opening checkout…" : `Get Pro · ${proPrice}`}
          </button>
        </div>
      </div>

      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mt-6 text-center">
        Secure checkout by Stripe · Cancel anytime · No hidden fees
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  { q: "Can it really work with WhatsApp?", a: "Yes. We use the WhatsApp Business Cloud API via Twilio. Voice notes in, voice notes out, plus text. You can test in seconds with a free sandbox number, then go live with a real WhatsApp Business number once Meta approves you." },
  { q: "Does my agent answer live phone calls?", a: "Not yet — today AgentReply handles voice notes and text on WhatsApp and the web. Live calls (where the agent picks up a ringing phone in real time) are on the roadmap as a paid add-on. Email support if you'd like early access." },
  { q: "How long does it take to build my own?", a: "Five minutes to a working demo. A day for production polish (custom voice, knowledge base, multi-language, intake form). A free 14-day trial gives you full access — no credit card needed." },
  { q: "What AI models power AgentReply?", a: "Claude Sonnet 4.5 for reasoning, OpenAI Whisper for transcription, OpenAI TTS for synthesis. Optional ElevenLabs premium voices and instant voice cloning available as add-ons. All swappable." },
  { q: "Is my data private?", a: "Yes. Conversations are stored only on your account, never used to train AI models. You can delete any contact or conversation at any time. GDPR-ready with a published Data Processing Addendum." },
  { q: "What languages does it speak?", a: "30+ languages out of the box. Set a default per agent, or let it auto-detect the language of each incoming message and reply in the same language." },
  { q: "Can I embed the chat widget on my website?", a: "Yes. One line of HTML adds a customisable floating chat bubble to Shopify, WordPress, Webflow, Wix, or any plain HTML site. Match your brand colours, upload your logo, set a launcher label." },
  { q: "How much does it cost?", a: "14-day free trial with full Pro access — no card required. Then $9.99/month, $70/year, or a one-time $250 lifetime plan. All plans include unlimited agents." },
];

function FaqSection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      {/* JSON-LD FAQPage schema for Google rich snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQ_ITEMS.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          }),
        }}
      />
      <div className="lg:col-span-5">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-4">04 / FAQ</div>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mb-8">
          Honest answers.<br /><span className="italic">No fluff.</span>
        </h2>
        <div className="aspect-[4/3] border border-[#F5F2EB]/10 overflow-hidden">
          <img src={CONTEXT_IMG} alt="" className="w-full h-full object-cover opacity-80" />
        </div>
      </div>
      <div className="lg:col-span-7 space-y-2">
        {FAQ_ITEMS.map((f, i) => (
          <details key={i} className="group border-b border-[#F5F2EB]/10 py-5" data-testid={`faq-${i}`}>
            <summary className="cursor-pointer flex justify-between items-center font-serif text-xl text-[#F5F2EB] group-hover:text-[#D95B38] transition-colors list-none">
              <span>{f.q}</span>
              <span className="font-mono text-2xl text-[#A39A8F] group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="font-mono text-sm text-[#A39A8F] mt-4 leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
    </div>
  );
}

